import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import SearchSelect from '@/components/common/SearchSelect';
import { STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';
import { CheckCircle2, AlertTriangle, XCircle, Calendar as CalendarIcon } from 'lucide-react';
import { accidentalConflicts } from './shiftSheetRules';
import { eligibilityState } from './createShiftServiceRules';   // UA-4-F9
import { EligibilityNote, LOCKED_ROW_CLASS } from './ScreeningBadge';   // BS3-005

// Status badge config — drives both colour and sort order.
const STATUS_META = {
  AVAILABLE:   { label: 'Available',   cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle2,  rank: 0 },
  PARTIAL:     { label: 'Partial',     cls: 'bg-amber-100 text-amber-800 border-amber-300',       Icon: AlertTriangle, rank: 1 },
  BOOKED:      { label: 'Booked',      cls: 'bg-slate-200 text-slate-700 border-slate-300',       Icon: CalendarIcon,  rank: 2 },
  UNAVAILABLE: { label: 'Unavailable', cls: 'bg-red-100 text-red-800 border-red-300',             Icon: XCircle,       rank: 3 },
  UNKNOWN:     { label: '',            cls: 'bg-slate-50 text-slate-500 border-slate-200',        Icon: null,          rank: 4 },
};

export function AvailabilityStatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.UNKNOWN;
  if (!meta.label) return null;
  const Icon = meta.Icon;
  return (
    <span
      className={'inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border font-medium ' + meta.cls}
      data-testid={'availability-status-' + status.toLowerCase()}
    >
      {Icon ? <Icon className="w-2.5 h-2.5" /> : null}
      {meta.label}
    </span>
  );
}

/**
 * Hook: load availability badges for a list of staff against a given shift slot.
 *
 *   const { statusMap, sortedStaff, loading, enabled } = useStaffAvailabilityForShift(staff, { date, startTime, endTime, excludeShiftId });
 *
 * - Respects feature flag `scheduling_availability_visibility_enabled`
 * - Returns the original staff list (unsorted, statusMap empty) when the flag is off OR the API errors
 * - Sort order: AVAILABLE → PARTIAL → BOOKED → UNAVAILABLE → unknown, then by full_name
 */
export function useStaffAvailabilityForShift(staff, { date, startTime, endTime, excludeShiftId } = {}) {
  const [statusMap, setStatusMap] = useState({});
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  // Fetch flag once.
  useEffect(() => {
    let mounted = true;
    api.get('/production/feature-flags/mine').then(r => {
      const f = r.data?.data || r.data || {};
      if (mounted) setEnabled(f.scheduling_availability_visibility_enabled !== false);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const userIds = useMemo(() => (staff || []).map(s => s.user_id || s.id).filter(Boolean), [staff]);

  useEffect(() => {
    if (!enabled || !date || userIds.length === 0) { setStatusMap({}); return; }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ date, user_ids: userIds.join(',') });
    if (startTime) params.set('start_time', startTime);
    if (endTime) params.set('end_time', endTime);
    if (excludeShiftId) params.set('exclude_shift_id', excludeShiftId);
    api.get('/availability/check-bulk?' + params.toString())
      .then(r => { if (!cancelled) setStatusMap(r.data?.data || {}); })
      .catch(() => { if (!cancelled) setStatusMap({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, date, startTime, endTime, excludeShiftId, userIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedStaff = useMemo(() => {
    if (!enabled || Object.keys(statusMap).length === 0) return staff || [];
    const arr = (staff || []).slice();
    arr.sort((a, b) => {
      const ka = a.user_id || a.id;
      const kb = b.user_id || b.id;
      const sa = statusMap[ka]?.status || 'UNKNOWN';
      const sb = statusMap[kb]?.status || 'UNKNOWN';
      const ra = STATUS_META[sa]?.rank ?? 4;
      const rb = STATUS_META[sb]?.rank ?? 4;
      if (ra !== rb) return ra - rb;
      return (a.full_name || '').localeCompare(b.full_name || '');
    });
    return arr;
  }, [staff, statusMap, enabled]);

  return { statusMap, sortedStaff, loading, enabled };
}

/**
 * Drop-in replacement for the staff <Select> in the Drawer / Create modal.
 * Renders each option with a status badge when the feature flag is on.
 */
export function StaffSelectWithAvailability({
  staff,
  value,
  onValueChange,
  placeholder = 'Select staff',
  filter,           // optional (s) => boolean
  triggerClass,
  testId,
  date,
  startTime,
  endTime,
  excludeShiftId,
  groupShiftIds,    // UC-A (§24–25): bookings inside this shared delivery group are intentional, never a conflict
  blockAccidental = false,  // when true, workers with an accidental conflict are shown disabled with the reason
  eligibilityShiftId,       // UA-4-F9: when set, one batch hard-eligibility read per shift; failures render disabled-with-reason
  eligibilityPreview,       // UA-4-F9: { date, participant_id, linked_participants, start_time, end_time } for a shift not yet created
}) {
  const filtered = useMemo(() => {
    return (staff || []).filter(s => (filter ? filter(s) : true));
  }, [staff, filter]);

  const { statusMap, sortedStaff, enabled } = useStaffAvailabilityForShift(filtered, {
    date, startTime, endTime, excludeShiftId,
  });
  const eligibility = useWorkerEligibility(filtered, eligibilityShiftId, eligibilityPreview);

  // NEW-007: searchable + paged (20 at a time); badges / reasons stay on each row.
  const options = sortedStaff.map(s => {
    const key = s.user_id || s.id;
    const status = enabled ? (statusMap[key]?.status) : null;
    const conflict = blockAccidental ? accidentalConflicts(statusMap, [{ id: key, name: s.full_name }], groupShiftIds)[0] : null;
    const intentional = blockAccidental && !conflict && (statusMap[key]?.booked_shifts || []).length > 0;   // overlaps only inside this group (§24)
    const elig = eligibilityState(key, eligibility);
    return {
      value: key,
      label: s.full_name || '',
      keywords: `${s.email || ''} ${s.employee_id || ''}`,
      disabled: !!conflict || elig.disabled,
      title: conflict?.reason || elig.reason || undefined,
      testid: elig.disabled ? `staff-option-ineligible-${key}` : (conflict ? `staff-option-conflict-${key}` : (intentional ? `staff-option-intentional-${key}` : undefined)),
      className: elig.screening ? LOCKED_ROW_CLASS : undefined,   // BS3-005: visible, locked, readable
      render: (
        <span className="inline-flex items-center gap-2 w-full">
          <span className="truncate">{s.full_name}</span>
          {status && !elig.disabled ? <AvailabilityStatusBadge status={status} /> : null}
          <EligibilityNote elig={elig} />
          {conflict ? <span className="text-[10px] text-red-700 truncate">{conflict.reason}</span> : null}
          {intentional ? <span className="text-[10px] text-sky-700">Intentional — same shared delivery group</span> : null}
        </span>
      ),
    };
  });

  return (
    <SearchSelect
      options={options}
      value={value}
      onChange={onValueChange}
      placeholder={placeholder}
      searchPlaceholder={STAFF_SEARCH_HINT}
      emptyText="No staff match that search"
      className={triggerClass}
      testId={testId}
    />
  );
}

/** UA-4-F9: one batch hard-eligibility read per shift (or preview payload) — { [workerId]: { eligible, block_codes, reason } }.
 *  Sensitive reasons arrive already redacted by the engine. Null while loading / when not applicable (pickers stay enabled). */
export function useWorkerEligibility(staff, shiftId, preview) {
  const [map, setMap] = useState(null);
  const [screening, setScreening] = useState(null);
  const ids = useMemo(() => (staff || []).map(s => s.user_id || s.id).filter(Boolean), [staff]);
  const idsKey = ids.join(',');
  // BS3-005: screening belongs to the worker, not the shift — load it once per staff list so unscreened staff are labelled and
  // locked even before a date / participant is chosen (the shift eligibility read below only runs once those are known).
  useEffect(() => {
    let mounted = true;
    if (!ids.length) { setScreening(null); return undefined; }
    api.post('/worker-matching/screening-states', { worker_ids: ids })
      .then(r => { if (mounted) setScreening(screeningEligibility(r.data?.data)); })
      .catch(() => { if (mounted) setScreening(null); });   // no permission / error → server still enforces at assignment
    return () => { mounted = false; };
  }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const previewKey = preview ? JSON.stringify([preview.date, preview.participant_id, preview.linked_participants, preview.start_time, preview.end_time]) : '';
  useEffect(() => {
    let mounted = true;
    if (!ids.length || (!shiftId && !(preview && preview.date && (preview.participant_id || (preview.linked_participants || []).length)))) { setMap(null); return undefined; }
    const req = shiftId
      ? api.get(`/worker-matching/shifts/${shiftId}/eligibility`, { params: { worker_ids: idsKey } })
      : api.post('/worker-matching/eligibility/preview', { ...preview, worker_ids: ids });
    req.then(r => { if (mounted) setMap(r.data?.data || null); }).catch(() => { if (mounted) setMap(null); });   // no permission / error → no pre-check; server still enforces at submit
    return () => { mounted = false; };
  }, [shiftId, idsKey, previewKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(() => (map || screening ? { ...(screening || {}), ...(map || {}) } : null), [map, screening]);
}

/** BS3-005: { workerId: MISSING|EXPIRED|UNVERIFIED } → eligibility rows (locked, with the screening state for the label). */
export function screeningEligibility(states) {
  const out = {};
  Object.entries(states || {}).forEach(([wid, st]) => {
    out[wid] = { eligible: false, block_codes: ['WORKER_SCREENING_INVALID'], reason: `Worker screening check is ${String(st).toLowerCase()}`, screening: st };
  });
  return out;
}
